import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { ExamCardExpiredModal } from '../../components';
import { PATHS } from '../../routes';
import { useAuth } from '../authentication';

interface ExamCardExpiredContextValue {
  isOpen: boolean;
  showExamCardExpiredModal: () => void;
}

const ExamCardExpiredContext = createContext<ExamCardExpiredContextValue | undefined>(undefined);

function ExamCardExpiredProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { logout, isLoading } = useAuth();

  const showExamCardExpiredModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleConfirm = useCallback(async () => {
    await logout();
    navigate(PATHS.Login);
  }, [logout, navigate]);

  const value = useMemo(
    () => ({
      isOpen,
      showExamCardExpiredModal,
    }),
    [isOpen, showExamCardExpiredModal],
  );

  return (
    <ExamCardExpiredContext.Provider value={value}>
      {children}
      <ExamCardExpiredModal
        open={isOpen}
        onConfirm={handleConfirm}
        isLoading={isLoading}
        countdownTime={120}
      />
    </ExamCardExpiredContext.Provider>
  );
}

function useExamCardExpired() {
  const context = useContext(ExamCardExpiredContext);

  if (context === undefined) {
    throw new Error('useExamCardExpired must be used within ExamCardExpiredProvider');
  }

  return context;
}

export { ExamCardExpiredProvider, useExamCardExpired };
export type { ExamCardExpiredContextValue };
